/**
 * HtmlEditor — TipTap-backed rich text editor used in
 *   - /outreach/templates  (MessageTemplate body field)
 *   - CampaignCreatorV2 step 1 (per-step body field)
 *
 * Emits HTML strings. Supports the mustache variables our backend renders
 * ({{first_name}}, {{full_name}}, {{company_name}}, {{pm_name}}, {{contact_title}})
 * via a "Insert variable" menu.
 *
 * The backend send-side `_compose_email_body` auto-detects HTML vs plain text,
 * so any output from this component flows straight through to Outlook.
 */
import React, { useEffect, useState } from 'react';
import { Box, Divider, Stack, Tooltip, IconButton, Menu, MenuItem, TextField,
         Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography }
       from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Icon } from '@iconify/react';

const VARS = [
  { key: 'first_name',    label: 'First name' },
  { key: 'last_name',     label: 'Last name' },
  { key: 'full_name',     label: 'Full name' },
  { key: 'company_name',  label: 'Company name' },
  { key: 'pm_name',       label: 'BA (sender) name' },
  { key: 'contact_title', label: 'Contact title' },
];

const ToolButton = ({ icon, active, onClick, tooltip, disabled }) => (
  <Tooltip title={tooltip}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          borderRadius: 0.75, p: 0.5, mx: 0.1,
          bgcolor: active ? 'action.selected' : 'transparent',
          color: active ? 'primary.main' : 'text.primary',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Icon icon={icon} width={16} />
      </IconButton>
    </span>
  </Tooltip>
);

export default function HtmlEditor({
  value,
  onChange,
  placeholder = 'Write your message…',
  minHeight = 160,
  // Hide the toolbar to embed in compact rows (still shows a tiny vars chip).
  compact = false,
}) {
  const [varMenuAnchor, setVarMenuAnchor] = useState(null);
  const [linkOpen, setLinkOpen]           = useState(false);
  const [linkUrl, setLinkUrl]             = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // We use our own Link extension to disable autolink (so {{first_name}} doesn't become a URL).
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink:    false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap's empty state is "<p></p>". Surface as empty string so backend
      // doesn't render a blank paragraph in the email.
      onChange?.(html === '<p></p>' ? '' : html);
    },
  });

  // Keep editor in sync when parent updates value externally (e.g. user picks
  // a template from the dropdown and the body field gets overwritten).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    // Avoid clobbering while the user is typing — only sync when the parent
    // value diverges and the editor is not currently focused.
    if (current !== incoming && !editor.isFocused) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const insertVar = (key) => {
    editor.chain().focus().insertContent(`{{${key}}}`).run();
    setVarMenuAnchor(null);
  };

  const applyLink = () => {
    const url = (linkUrl || '').trim();
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const final = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: final }).run();
    }
    setLinkOpen(false);
    setLinkUrl('');
  };

  return (
    <Box sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
      '&:focus-within': { borderColor: 'primary.main' },
      transition: 'border-color 0.15s',
    }}>
      {!compact && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.25,
          px: 0.75, py: 0.4,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'background.neutral',
          flexWrap: 'wrap',
        }}>
          <ToolButton icon="eva:text-fill"  tooltip="Bold"          active={editor.isActive('bold')}
                      onClick={() => editor.chain().focus().toggleBold().run()} />
          <ToolButton icon="mdi:format-italic" tooltip="Italic"      active={editor.isActive('italic')}
                      onClick={() => editor.chain().focus().toggleItalic().run()} />
          <ToolButton icon="mdi:format-strikethrough" tooltip="Strike" active={editor.isActive('strike')}
                      onClick={() => editor.chain().focus().toggleStrike().run()} />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

          <ToolButton icon="mdi:format-header-2" tooltip="Heading"   active={editor.isActive('heading', { level: 2 })}
                      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
          <ToolButton icon="mdi:format-list-bulleted" tooltip="Bullet list" active={editor.isActive('bulletList')}
                      onClick={() => editor.chain().focus().toggleBulletList().run()} />
          <ToolButton icon="mdi:format-list-numbered" tooltip="Numbered list" active={editor.isActive('orderedList')}
                      onClick={() => editor.chain().focus().toggleOrderedList().run()} />
          <ToolButton icon="mdi:format-quote-close" tooltip="Quote"   active={editor.isActive('blockquote')}
                      onClick={() => editor.chain().focus().toggleBlockquote().run()} />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

          <ToolButton icon="mdi:link-variant" tooltip="Add / edit link"
                      active={editor.isActive('link')}
                      onClick={() => {
                        setLinkUrl(editor.getAttributes('link')?.href || '');
                        setLinkOpen(true);
                      }} />
          <ToolButton icon="mdi:link-variant-off" tooltip="Remove link"
                      disabled={!editor.isActive('link')}
                      onClick={() => editor.chain().focus().unsetLink().run()} />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

          <Button
            size="small" variant="text"
            onClick={(e) => setVarMenuAnchor(e.currentTarget)}
            sx={{ minWidth: 0, textTransform: 'none', fontSize: 12, height: 26 }}
            endIcon={<Icon icon="eva:chevron-down-fill" width={14} />}
          >
            Insert variable
          </Button>
          <Menu
            anchorEl={varMenuAnchor}
            open={Boolean(varMenuAnchor)}
            onClose={() => setVarMenuAnchor(null)}
          >
            {VARS.map((v) => (
              <MenuItem key={v.key} onClick={() => insertVar(v.key)} dense>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{v.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {`{{${v.key}}}`}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>

          <Box sx={{ flex: 1 }} />

          <ToolButton icon="mdi:format-clear" tooltip="Clear formatting"
                      onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} />
          <ToolButton icon="mdi:undo" tooltip="Undo"
                      onClick={() => editor.chain().focus().undo().run()}
                      disabled={!editor.can().undo()} />
          <ToolButton icon="mdi:redo" tooltip="Redo"
                      onClick={() => editor.chain().focus().redo().run()}
                      disabled={!editor.can().redo()} />
        </Box>
      )}

      <Box
        sx={{
          minHeight,
          px: 1.5, py: 1,
          fontSize: 14, lineHeight: 1.55,
          '& .ProseMirror': { minHeight, outline: 'none' },
          '& .ProseMirror p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
          '& a':  { color: 'primary.main' },
          '& h2': { fontSize: 18, fontWeight: 700, margin: '12px 0 6px 0' },
          '& ul, & ol': { paddingLeft: 20, margin: '0 0 12px 0' },
          '& blockquote': {
            borderLeft: '3px solid', borderColor: 'divider',
            paddingLeft: 12, color: 'text.secondary',
            margin: '0 0 12px 0',
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Insert link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small"
            label="URL" placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyLink(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyLink}>Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
